import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import firebaseApp from "../../firebase/credenciales";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { 
  Container, 
  Card, 
  Form, 
  Button, 
  Spinner, 
  Alert, 
  Table, 
  Row, 
  Col, 
  Badge,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { FaTrashAlt, FaSearch, FaExclamationTriangle, FaFileAlt, FaEye } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const MasivosEliminar = ({ usuario }) => {
  const db = getFirestore(firebaseApp);
  const storage = getStorage(firebaseApp);

  const [ruc, setRuc] = useState("");
  const [ano, setAno] = useState("");
  const [mes, setMes] = useState("");
  const [fecha, setFecha] = useState("");
  const [semana, setSemana] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState("");
  const [boletas, setBoletas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (usuario?.ruc) {
      setRuc(usuario.ruc);
    }
  }, [usuario]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(boletas.map((b) => b.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const getStoragePath = (url) => {
    try {
      const matches = url.match(/\/o\/(.*?)\?/);
      if (matches && matches[1]) {
        return decodeURIComponent(matches[1]);
      }
    } catch (e) {}
    return null;
  };

  const parseFechaDocToYmd = (rawFecha) => {
    if (!rawFecha) return null;

    // Caso 1: Firestore Timestamp
    if (typeof rawFecha?.toDate === "function") {
      const d = rawFecha.toDate();
      if (Number.isNaN(d?.getTime?.())) return null;
      const y = String(d.getFullYear());
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }

    // Caso 2: String tipo "19-06-2024,08:08:037"
    const asString = String(rawFecha).trim();
    const ddMmYyyyMatch = asString.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (ddMmYyyyMatch) {
      const [, dd, mm, yyyy] = ddMmYyyyMatch;
      return `${yyyy}-${mm}-${dd}`;
    }

    // Caso 3: Cualquier fecha parseable por Date
    const fallbackDate = new Date(asString);
    if (!Number.isNaN(fallbackDate.getTime())) {
      const y = String(fallbackDate.getFullYear());
      const m = String(fallbackDate.getMonth() + 1).padStart(2, "0");
      const day = String(fallbackDate.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }

    return null;
  };

  // Buscar boletas antes de eliminar
  const handleBuscar = async () => {
    if (!ano || !mes) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor complete al menos el año y el mes para realizar la búsqueda",
        showConfirmButton: true,
      });
      return;
    }

    setBuscando(true);
    setResultado("");
    setBoletas([]);
    setSelectedIds([]);
    
    try {
      // Construir query dinámicamente
      let q = query(
        collection(db, "document"),
        where("ruc", "==", ruc),
        where("ano", "==", ano),
        where("periodo", "==", mes)
      );
      
      // Agregar filtro de semana solo si está especificado
      if (semana.trim() !== "") {
        q = query(q, where("semana", "==", semana));
      }

      const docsSnap = await getDocs(q);
      let lista = docsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(item => item.activo !== false);

      // Filtrar por día exacto en cliente (fechaDoc guardada como string dd-mm-yyyy,...)
      if (fecha) {
        const yyyyMmDd = String(fecha);
        lista = lista.filter((item) => parseFechaDocToYmd(item?.fechaDoc) === yyyyMmDd);
      }

      setBoletas(lista);
      const criterioSemana = semana.trim() !== "" ? ` y semana ${semana}` : "";
      const criterioFecha = fecha ? ` y fecha ${fecha}` : "";
      setResultado(`Se encontraron ${lista.length} documentos${criterioSemana}${criterioFecha}.`);
      
      if (lista.length === 0) {
        Swal.fire({
          icon: "info",
          title: "Sin resultados",
          text: "No se encontraron documentos con los criterios especificados",
          showConfirmButton: true,
        });
      }
    } catch (error) {
      setResultado("Error al buscar documentos: " + error.message);
      Swal.fire({
        icon: "error",
        title: "Error en la búsqueda",
        text: error.message,
        showConfirmButton: true,
      });
    }
    setBuscando(false);
  };

  // Eliminar boletas en lotes para evitar cuelgues
  const handleDelete = async () => {
    const targets = boletas.filter((b) => selectedIds.includes(b.id));

    if (targets.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Sin selección",
        text: "Por favor seleccione al menos un documento para eliminar.",
        showConfirmButton: true,
      });
      return;
    }

    const criterioSemana = semana.trim() !== "" ? ` y semana ${semana}` : "";
    const criterioFecha = fecha ? ` y fecha ${fecha}` : "";

    const result = await Swal.fire({
      title: '⚠️ Confirmación de Eliminación Seleccionada',
      html: `
        <div class="text-start">
          <p><strong>¿Está seguro de eliminar los ${targets.length} documento(s) seleccionado(s)?</strong></p>
          <p class="text-danger"><strong>Esta acción es IRREVERSIBLE</strong></p>
          <ul class="text-muted">
            <li>Se eliminarán los documentos de la base de datos</li>
            <li>Se eliminarán los archivos del almacenamiento</li>
            <li>Los trabajadores perderán acceso a estos documentos</li>
          </ul>
          <p class="text-warning"><strong>Criterios de búsqueda aplicados:</strong></p>
          <ul class="text-muted">
            <li><strong>Año:</strong> ${ano}</li>
            <li><strong>Mes:</strong> ${mes}${criterioSemana}${criterioFecha}</li>
          </ul>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar seleccionados',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      width: '600px'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    setResultado("");
    let eliminados = 0;
    const batchSize = 10;
    
    try {
      for (let i = 0; i < targets.length; i += batchSize) {
        const batch = targets.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (data) => {
            // Soft-delete: desactivar el documento en Firestore
            await updateDoc(doc(db, "document", data.id), {
              activo: false
            });
            eliminados++;
          })
        );
      }
      
      const criterioEliminacionSemana = semana.trim() !== "" ? ` de la semana ${semana}` : "";
      const criterioEliminacionFecha = fecha ? ` del día ${fecha}` : "";
      setResultado(`Se desactivaron ${eliminados} documentos seleccionados${criterioEliminacionSemana}${criterioEliminacionFecha}.`);
      
      // Filtrar las boletas eliminadas de la lista local y resetear selección
      setBoletas((prev) => prev.filter((b) => !selectedIds.includes(b.id)));
      setSelectedIds([]);
      
      // AUDITORÍA
      try {
        await logBusinessAction("ELIMINACION_MASIVA_DOCUMENTOS", "GestionDocumental", {
          eliminados: eliminados,
          ano: ano,
          mes: mes,
          fecha: fecha || null,
          semana: semana || null,
          tipoEliminacion: "desactivar_seleccionados",
          documentosSeleccionados: targets.map(t => `${t.nombre || t.empleado || 'Sin nombre'} (${t.dni || 'S/D'})`).join(", ")
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría", e);
      }
      
      Swal.fire({
        icon: "success",
        title: "Eliminación completada",
        text: `Se eliminaron exitosamente ${eliminados} documentos`,
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (error) {
      setResultado("Error al eliminar documentos: " + error.message);
      Swal.fire({
        icon: "error",
        title: "Error en la eliminación",
        text: error.message,
        showConfirmButton: true,
      });
    }
    setLoading(false);
  };

  const limpiarFormulario = () => {
    setAno("");
    setMes("");
    setFecha("");
    setSemana("");
    setBoletas([]);
    setSelectedIds([]);
    setResultado("");
  };

  const anoSeleccionado = ano || "-";
  const mesSeleccionado = mes || "-";

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado verde oscuro como Reglamentos */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <RiDeleteBin6Fill className="me-3" />
                Eliminación Masiva de Documentos
              </h2>
              <p className="mb-0 opacity-75">
                <FaExclamationTriangle className="me-2" />
                Elimina múltiples documentos por criterios específicos - Acción irreversible
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Alerta de advertencia */}
            <Alert variant="danger" className="mb-4" style={{ borderRadius: "15px" }}>
              <div className="d-flex align-items-center">
                <FaExclamationTriangle size={24} className="me-3" />
                <div>
                  <strong>⚠️ ZONA DE PELIGRO</strong>
                  <p className="mb-0 mt-1">
                    Esta función eliminará permanentemente documentos del sistema. 
                    Los trabajadores perderán acceso a estos documentos. Use con extrema precaución.
                  </p>
                </div>
              </div>
            </Alert>

            {/* Barra superior con información */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        color: "white"
                      }}
                    >
                      <RiDeleteBin6Fill size={24} />
                    </div>
                  </Col>
                  <Col md={9}>
                    <h5 className="mb-1 fw-semibold">Eliminación Masiva de Documentos</h5>
                    <p className="text-muted mb-0 small">
                      Busca y elimina documentos por año, mes y fecha de envío (fechaDoc)
                    </p>
                  </Col>
                  <Col md={2} className="text-end">
                    <Button
                      variant="outline-secondary"
                      onClick={limpiarFormulario}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px" }}
                    >
                      <FaSearch size="16px" />
                      Limpiar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Formulario de criterios */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Header 
                className="text-white py-3"
                style={{ 
                  background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                  borderRadius: "15px 15px 0 0"
                }}
              >
                <div className="d-flex align-items-center">
                  <FaSearch className="me-2" />
                  <div>
                    <h5 className="mb-0 fw-semibold">Criterios de Búsqueda</h5>
                    <small className="opacity-75">Define los parámetros para localizar documentos</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-bold">RUC de la Empresa</Form.Label>
                      <Form.Control
                        value={ruc}
                        disabled
                        className="form-control-lg"
                        style={{ borderRadius: "10px", backgroundColor: "#f8f9fa" }}
                      />
                      <small className="text-muted">Campo automático basado en su empresa</small>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Año</Form.Label>
                      <Form.Control
                        value={ano}
                        onChange={(e) => setAno(e.target.value)}
                        placeholder="Ej: 2024"
                        className="form-control-lg"
                        style={{ borderRadius: "10px" }}
                        maxLength="4"
                        pattern="[0-9]{4}"
                      />
                      <small className="text-muted">Año del período de la boleta</small>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Mes</Form.Label>
                      <Form.Control
                        value={mes}
                        onChange={(e) => setMes(e.target.value)}
                        placeholder="Ej: 06"
                        className="form-control-lg"
                        style={{ borderRadius: "10px" }}
                        maxLength="2"
                        pattern="[0-9]{2}"
                      />
                      <small className="text-muted">Mes del período de la boleta (01-12)</small>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Fecha</Form.Label>
                      <Form.Control
                        type="date"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        className="form-control-lg"
                        style={{ borderRadius: "10px" }}
                      />
                      <small className="text-muted">Opcional: filtra por fechaDoc (fecha de envío)</small>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Semana <small className="text-muted">(Opcional)</small></Form.Label>
                      <Form.Control
                        value={semana}
                        onChange={(e) => setSemana(e.target.value)}
                        placeholder="Ej: SEM36 (opcional)"
                        className="form-control-lg"
                        style={{ borderRadius: "10px" }}
                      />
                      <small className="text-muted">Deja vacío para eliminar todo el mes</small>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <Row className="justify-content-center">
                  <Col md={6}>
                    <Button
                      variant="primary"
                      onClick={handleBuscar}
                      disabled={buscando || loading}
                      className="w-100 d-flex align-items-center justify-content-center gap-2"
                      size="lg"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        border: "none",
                      }}
                    >
                      {buscando ? <Spinner animation="border" size="sm" /> : <FaSearch />}
                      {buscando ? "Buscando..." : "Buscar Documentos"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estado de carga */}
            {(buscando || loading) && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Body className="p-4">
                  <div className="text-center">
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        color: "white"
                      }}
                    >
                      <Spinner animation="border" variant="light" />
                    </div>
                    <h5 className="mb-2">
                      {buscando ? "Buscando documentos..." : "Eliminando documentos..."}
                    </h5>
                    <p className="text-muted mb-0">
                      {buscando 
                        ? "Por favor espera mientras localizamos los documentos" 
                        : "Por favor espera mientras se eliminan los documentos"}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Resultado */}
            {resultado && (
              <Alert 
                variant={boletas.length > 0 ? "success" : "info"} 
                className="mb-4" 
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaFileAlt className="me-2" />
                  {resultado}
                </div>
              </Alert>
            )}

            {/* Estadísticas */}
            {boletas.length > 0 && (
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)", color: "white", borderRadius: "15px" }}>
                      <h4 className="fw-bold">{boletas.length}</h4>
                      <p className="mb-0">Documentos Encontrados</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)", color: "white", borderRadius: "15px" }}>
                      <h4 className="fw-bold">{anoSeleccionado}/{mesSeleccionado}</h4>
                      <p className="mb-0">Período Seleccionado</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                    <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)", color: "white", borderRadius: "15px" }}>
                      <h4 className="fw-bold">{fecha || "-"}</h4>
                      <p className="mb-0">Fecha de Envío (fechaDoc)</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Tabla de documentos encontrados */}
            {boletas.length > 0 && (
              <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                <Card.Header 
                  className="text-white py-3"
                  style={{ 
                    background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                    borderRadius: "15px 15px 0 0"
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaFileAlt className="me-2" />
                    <div className="flex-grow-1">
                      <h5 className="mb-0 fw-semibold">Documentos Encontrados</h5>
                      <small className="opacity-75">Documentos que serán eliminados</small>
                    </div>
                    <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                      {boletas.length} documentos
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                      <thead 
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th className="border-0 py-3 px-3 text-center" style={{ width: '5%' }}>
                            <Form.Check
                              type="checkbox"
                              checked={boletas.length > 0 && selectedIds.length === boletas.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '8%' }}>N°</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: '30%' }}>Empleado</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '12%' }}>Período</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '15%' }}>Semana</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '15%' }}>DNI</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '15%' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boletas.map((boleta, index) => (
                          <tr key={boleta.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="text-center py-3 px-3">
                              <Form.Check
                                type="checkbox"
                                checked={selectedIds.includes(boleta.id)}
                                onChange={() => handleSelectOne(boleta.id)}
                              />
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="dark" className="px-2 py-1">
                                {index + 1}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex align-items-center">
                                <div 
                                  className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "35px",
                                    height: "35px",
                                    background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                                    color: "white"
                                  }}
                                >
                                  <FaFileAlt size="14px" />
                                </div>
                                <div>
                                  <div className="fw-medium text-dark small">
                                    {boleta.nombre || boleta.empleado || "Sin nombre"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="info" className="px-2 py-1">
                                {boleta.periodo}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="warning" text="dark" className="px-2 py-1">
                                {boleta.semana}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-3">
                              <Badge bg="secondary" className="px-2 py-1">
                                {boleta.dni || "-"}
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-3">
                              {boleta.link ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Ver documento</Tooltip>}
                                >
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    href={boleta.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ borderRadius: "8px" }}
                                  >
                                    <FaEye size="14px" />
                                  </Button>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted small">Sin enlace</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-end">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Esta acción desactivará los documentos seleccionados</Tooltip>}
                  >
                    <Button
                      variant="danger"
                      onClick={handleDelete}
                      disabled={loading || selectedIds.length === 0}
                      className="d-flex align-items-center gap-2"
                      size="lg"
                      style={{ borderRadius: "10px" }}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : <RiDeleteBin6Fill />}
                      {loading ? "Eliminando..." : `Eliminar Seleccionados (${selectedIds.length})`}
                    </Button>
                  </OverlayTrigger>
                </Card.Footer>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default MasivosEliminar;